import { Injectable } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { shareReplay, take } from 'rxjs/operators'
import { Footnote } from 'src/app/models/footnote.model'
import { AccountProfileService } from 'src/app/services/accountProfile/account-profile.service'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FootnoteService } from 'src/app/services/footnotes/footnotes.service'
import { MainService } from 'src/app/services/main.service'

const profIndex = 0
const perfIndex = 1
const expIndex = 2
const feeIndex = 3
const minimumIndex = 4
const contentIndex = 5
const riskResponseIndex = 6
const marketPriceContentIndex = 7

@Injectable({
  providedIn: 'root',
})
export class PerformanceTabService {
  performanceModel: any
  aemContentSub: Subscription
  footNoteAem: AEMChunk
  rSquaredAndBeta: AEMChunk
  expenseRatio: AEMChunk
  acquiredFundFeesAndExpenses: AEMChunk

  constructor(
    private readonly mainService: MainService,
    private readonly footnoteService: FootnoteService,
    private readonly centralContentService: CentralContentService,
    private readonly accountProfileService: AccountProfileService,
  ) {}

  getModelData(): Observable<any> {
    return new Observable((subscriber) => {
      this.mainService
        .getPerformanceObservable()
        .pipe(take(1))
        .subscribe((perfObs) => {
          if (
            Object.keys(perfObs).length !== 0 &&
            perfObs[0]?.name !== 'HTTPError' &&
            this.performanceModel === undefined
          ) {
            if (perfObs[profIndex]?.fundProfile.isExternalFund) {
              this.accountProfileService
                .getAccountProfile(perfObs[profIndex]?.fundProfile.ticker)
                .pipe(shareReplay())
                .subscribe((response) => {
                  this.performanceModel.accountProfile = response
                })
            }

            this.performanceModel = {
              performance: perfObs[perfIndex],
              expenseRatio: perfObs[expIndex]?.expenseRatio,
              avgExpenseRatio: perfObs[expIndex]?.avgExpenseRatio,

              profileData: {
                ticker: perfObs[profIndex]?.fundProfile.ticker,
                fundId: perfObs[profIndex]?.fundProfile.fundId,
                shortName: perfObs[profIndex]?.fundProfile.shortName,
                isMutualFund: perfObs[profIndex]?.fundProfile?.isMutualFund,
                isETF: perfObs[profIndex]?.fundProfile?.isETF,
                is529: perfObs[profIndex]?.fundProfile?.is529,
                isInternal: perfObs[profIndex]?.fundProfile?.isInternalFund,
                isExternal: perfObs[profIndex]?.fundProfile?.isExternalFund,
                isFundOfFunds:
                  perfObs[profIndex]?.fundProfile?.fundFact?.isFundOfFunds,
                isMoneyMarket:
                  perfObs[profIndex]?.fundProfile?.fundFact?.isMoneyMarket,
                fundName: perfObs[profIndex]?.fundProfile?.longName,
                isInternationalStockFund:
                  perfObs[profIndex]?.fundProfile?.fundFact
                    ?.isInternationalStockFund,
                isInternational:
                  perfObs[profIndex]?.fundProfile?.fundFact?.isInternational,
                isGlobal: perfObs[profIndex]?.fundProfile?.fundFact?.isGlobal,
                isNTFFund: perfObs[profIndex]?.fundProfile?.fundFact?.isNTFFund,
                isLoadFund:
                  perfObs[profIndex]?.fundProfile?.fundFact?.isLoadFund,
                isFundTransferableToVGI:
                  perfObs[profIndex]?.fundProfile?.fundFact
                    ?.isFundTransferableToVGI,
                isFundAccessFund:
                  perfObs[profIndex]?.fundProfile?.fundFact?.isFundAccessFund,
                isMarketNeutralFund:
                  perfObs[profIndex]?.fundProfile?.fundFact
                    ?.isMarketNeutralFund,
                fundCategoryId:
                  perfObs[profIndex]?.fundProfile?.fundCategory?.low?.id,
              },
              fees: perfObs[feeIndex]?.fees,
              minimum: perfObs[minimumIndex]?.general?.initial,
              minimums: perfObs[minimumIndex],
              contentBenchmarkFootNote:
                perfObs[contentIndex]?.benchmarkFootnote,
              contentPerformanceFootNote:
                perfObs[contentIndex]?.performanceFootnote,
              riskResponse: perfObs[riskResponseIndex],
              contentMarketNeutralFootNote:
                perfObs[contentIndex]?.marketNeutralFootNote,
              contentMarketPriceFootNote: perfObs[marketPriceContentIndex],
            }
            this.setFootnotes()
          }
          subscriber.next(this.performanceModel)
          subscriber.complete()
        })
    })
  }
  parseAEMContent(content: any) {
    this.footNoteAem = this.centralContentService.getContentByBody(
      'footnote_avg_expense_ratio_similar_funds',
      content,
    )
    this.rSquaredAndBeta = this.centralContentService.getContentByBody(
      'footnote_r_squared_and_beta',
      content,
    )
    if (
      this.performanceModel.profileData.ticker === 'VASFX' ||
      this.performanceModel.profileData.ticker === 'VMNIX' ||
      this.performanceModel.profileData.ticker === 'VMNFX'
    ) {
      this.expenseRatio = {
        contentValue: this.performanceModel.contentMarketNeutralFootNote,
        isAEM: true,
      }
    } else {
      this.expenseRatio = this.centralContentService.getContentByBody(
        'footnote_expense_ratio',
        content,
      )
    }
    this.acquiredFundFeesAndExpenses =
      this.centralContentService.getContentByBody(
        'footnote_acquired_fund_fees_and_expenses',
        content,
      )
  }
  setFootnotes() {
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
    this.setMarketPriceFootnote()
    this.setBenchmarkFootnote()
    this.setPerformanceFootnote()
    this.setExpenseRatioFootnote()
    this.setRsquaredBetaFootnote()
  }
  setPerformanceFootnote(): void {
    if (this.performanceModel?.contentPerformanceFootNote) {
      this.performanceModel.performanceFootnote = new Footnote(
        this.performanceModel.contentPerformanceFootNote,
        'performance',
        false,
        'footnote_performance',
      )
      this.footnoteService.addFootnote(
        this.performanceModel.performanceFootnote,
      )
    }
  }
  setBenchmarkFootnote() {
    if (this.performanceModel?.contentBenchmarkFootNote) {
      this.performanceModel.benchmarkFootnote = new Footnote(
        this.performanceModel.contentBenchmarkFootNote,
        'performance',
        false,
        'footnote_benchmark',
      )
      this.footnoteService.addFootnote(this.performanceModel.benchmarkFootnote)
    }
  }
  // eslint-disable-next-line complexity
  setExpenseRatioFootnote() {
    if (this.performanceModel === undefined) {
      return
    } else if (
      this.performanceModel?.profileData?.isFundOfFunds &&
      this.performanceModel.profileData.isMutualFund
    ) {
      this.performanceModel.expenseRatioFootnote = new Footnote(
        this.acquiredFundFeesAndExpenses.contentValue,
        'performance',
        this.acquiredFundFeesAndExpenses.isAEM,
        'footnote_acquired_fund_fees_and_expenses',
      )
      this.footnoteService.addFootnote(
        this.performanceModel.expenseRatioFootnote,
      )
    } else {
      if (
        this.performanceModel.profileData.isMarketNeutralFund ||
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        this.performanceModel.profileData.fundCategoryId === 1416
      ) {
        this.performanceModel.expenseRatioFootnote = new Footnote(
          this.expenseRatio.contentValue,
          'performance',
          this.expenseRatio.isAEM,
          'footnote_expense_ratio',
        )
        this.footnoteService.addFootnote(
          this.performanceModel.expenseRatioFootnote,
        )
      }
    }
    this.performanceModel.expenseRatioFootnote = new Footnote(
      this.footNoteAem.contentValue,
      'performance',
      this.footNoteAem.isAEM,
      'footnote_avg_expense_ratio_similar_funds',
    )
    this.footnoteService.addFootnote(this.performanceModel.expenseRatioFootnote)
  }
  setRsquaredBetaFootnote() {
    if (this.performanceModel?.riskResponse?.volatility?.asOfDate) {
      this.performanceModel.rSquaredBetaFootnote = new Footnote(
        this.rSquaredAndBeta.contentValue,
        'performance',
        this.rSquaredAndBeta.isAEM,
        'footnote_r_squared_and_beta',
      )
      this.footnoteService.addFootnote(
        this.performanceModel.rSquaredBetaFootnote,
      )
    }
  }
  setMarketPriceFootnote(): void {
    if (
      this.performanceModel?.contentMarketPriceFootNote &&
      this.performanceModel?.profileData?.isETF &&
      !this.performanceModel?.profileData?.isExternal
    ) {
      this.performanceModel.marketPriceFootNote = new Footnote(
        this.performanceModel.contentMarketPriceFootNote['@marketPriceReturns'],
        'performance',
        false,
        'footnote_market_price',
      )
      this.footnoteService.addFootnote(
        this.performanceModel.marketPriceFootNote,
      )
    }
  }
}
